import { Suspense, useRef } from 'react'
import * as THREE from 'three'
import { Vector3 } from 'three'
import { useVerandaSelector } from '../../../..'
import { Helper } from '../../../../classes/helper'
import { useMeasures } from '../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../hooks/state/useRoof'
import { Shading } from '../shading/Shading'
import { DynamicBar } from './bar/DynamicBar'
import { RoofBar } from './bar/RoofBar'
import { RoofBarOuter } from './bar/RoofBarOuter'
import { RoofBeams } from './bar/RoofBeams'
import { RoofGlass } from './glass/RoofGlass'
import { SinusPlatte } from './plates/SinusPlatte'
import { TrapezPlatte } from './plates/TrapezPlatte'
import { Slats } from './slats/Slats'
import { Panel } from './solar/Panel'

export const Roof = (): JSX.Element => {
  const {
    polesTopNumber,
    userWidth,
    verandaTyp,
    userAngle,
    hasDynamicBar,
    roofLogWidth,
    isQubus,
    userHeight,
    attachedSize,
    angleCompensation,
    offsetRoofLog,
    hasLedStripe,
    depth,
    attachment,
  } = useMeasures()

  const {
    roofGlassDepth,
    roofCoverings,
    defaultRoofCoverings,
    amplitude,
    ledLight,
    ledLightColor,
    slatAngle,
  } = useRoof()

  const nightMode = useVerandaSelector((state) => state.scene.nightMode)
  const ref = useRef()

  const lightOn = nightMode && ((ledLight && hasLedStripe) || ledLight)

  const shadingMode = useVerandaSelector((state) => state.scene.shadingMode)

  const poles = polesTopNumber - 1 > 0 ? polesTopNumber - 1 : 2
  const length = roofLogWidth - 0.01
  const hasLamellenPlate = roofCoverings.lamellen

  const roofBarSize = {
    length: roofLogWidth,
    width: verandaTyp.roofBar.width,
    deg: userAngle,
    barDepth: verandaTyp.roofBar.depth,
    rad: (userAngle * Math.PI) / 180,
    offset: Math.min(
      Math.tan((userAngle * Math.PI) / 180) * verandaTyp.roofBar.width,
      length / 2
    ),
  }
  const barHeight = verandaTyp.bar.width
    ? verandaTyp.bar.width
    : -angleCompensation * 2 + offsetRoofLog.c

  const roofPosition = isQubus
    ? [
        0,

        userHeight +
          (defaultRoofCoverings
            ? -offsetRoofLog.c + barHeight
            : verandaTyp.bar.width
            ? verandaTyp.bar.width
            : +offsetRoofLog.c),
        verandaTyp.log.depth,
      ]
    : [0, userHeight, attachedSize.z]

  const startPosition = new Vector3(
    roofPosition[0],
    roofPosition[1],
    roofPosition[2]
  )

  const ledPosition = hasLamellenPlate
    ? new Vector3(
        shadingMode ? 0 : -userHeight / 2 + 0.2,
        shadingMode
          ? -0.1
          : hasLamellenPlate
          ? slatAngle
            ? depth - 0.3
            : userWidth / 2 - 0.1
          : -0.1,
        0 - (attachment.free ? 0.5 : 0)
      )
    : new Vector3(
        0,
        -angleCompensation,
        depth / 2 - (attachment.free ? 0.5 : -0.1)
      )

  return (
    <group
      position={startPosition}
      rotation={[Helper.convertAngleToRadiant(userAngle!), 0, 0]}
    >
      {[...Array(poles)].map((_, index) => {
        return (
          <group key={`poles${index}`}>
            <RoofBar positionX={index} size={roofBarSize} />
            <Suspense fallback={null}>
              {roofCoverings.solar && <Panel positionX={index} />}
              {(roofCoverings.glas || roofCoverings.poly) && (
                <RoofGlass positionX={index} poles={poles} />
              )}
            </Suspense>
          </group>
        )
      })}

      {roofCoverings.lamellen && (
        <Suspense fallback={null}>
          <Slats />
        </Suspense>
      )}

      {(roofCoverings.welle || roofCoverings.trapez) && (
        <group
          position={[
            0,
            isQubus
              ? -verandaTyp.roofBar.width / 2 -
                roofGlassDepth -
                (verandaTyp.bar.width ? amplitude / 100 : 0)
              : verandaTyp.roofBar.width / 2 + roofGlassDepth,
            0,
          ]}
        >
          <Suspense fallback={null}>
            {roofCoverings.welle ? <SinusPlatte /> : <TrapezPlatte />}

            <RoofBeams roofBarSize={roofBarSize} />
          </Suspense>
        </group>
      )}

      {!(
        (roofCoverings.lamellen ||
          roofCoverings.welle ||
          roofCoverings.trapez) &&
        isQubus
      ) && <RoofBarOuter size={roofBarSize} />}

      <Suspense fallback={null}>
        <Shading />
      </Suspense>

      {hasDynamicBar && <DynamicBar offset={roofBarSize.offset} />}

      {lightOn && (
        <group
          rotation={[
            THREE.MathUtils.degToRad(userAngle!),
            Helper.convertAngleToRadiant(
              slatAngle && hasLamellenPlate ? 90 : 0
            ),
            shadingMode
              ? 0
              : Helper.convertAngleToRadiant(hasLamellenPlate ? 90 : 0),
          ]}
        >
          <rectAreaLight
            ref={ref as any}
            position={ledPosition}
            width={userWidth - 0.5}
            height={userHeight + angleCompensation}
            castShadow={false}
            color={ledLightColor}
            intensity={2.7}
            rotation={[
              -Math.PI / 2 - Helper.convertAngleToRadiant(userAngle),
              0,
              0,
            ]}
          ></rectAreaLight>
        </group>
      )}
    </group>
  )
}
